import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";

import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Container } from "@/design-system/Container";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Tiles } from "@/design-system/Tiles";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useConfig } from "@/providers/config/useConfig";
import { usePortfolioQueries } from "@/queries/portfolio/queryOptions";
import { useTranslation } from "@/translations";

import { AvailableIntegrationCard } from "./AvailableIntegrationCard";

/** Renders the portfolio integration catalogue using the configured content source. */
export function AvailableIntegrationsPage(): React.ReactNode {
  const { t } = useTranslation();

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.available-integrations.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.portfolio.title"),
            },
            {
              name: t("page.available-integrations.title"),
            },
          ]}
        />
      }
      actions={
        <AskNobiButton
          defaultFilters={{}}
          context={{ scope: "portfolio", entity: "integrations", page: "list" }}
          suggestions={[
            t("component.ask-nobi-chat-drawer.integrations.suggestion.1"),
            t("component.ask-nobi-chat-drawer.integrations.suggestion.2"),
          ]}
        />
      }
    >
      <AvailableIntegrationsContent />
    </DocumentPage>
  );
}

/** Loads available integrations using the session language and configured Prepr Dashboard URL, then renders the tab. */
export function AvailableIntegrationsContent(): React.ReactNode {
  const sessionUser = useSessionUser();
  const postHog = usePostHog();
  const preprUrl = useConfig("preprDashboardUrl");
  const portfolioQueries = usePortfolioQueries();
  const { data, isLoading, error } = useQuery(
    portfolioQueries.availableIntegrationList(preprUrl, sessionUser.language.id),
  );

  if (error) {
    return <ErrorPage error={error} />;
  }
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <Container fullWidth>
      <Tiles columns={{ base: 1, container: { md: 2, xl: 3 } }} gap="md">
        {data?.map((integration) => (
          <AvailableIntegrationCard
            key={integration._id}
            integration={integration}
            onClick={() => {
              postHog.capture("clicked_integration_card", {
                integration: integration._slug,
              });
            }}
          />
        ))}
      </Tiles>
    </Container>
  );
}
